// Safe storage wrapper that falls back to in-memory storage if localStorage is blocked or throws errors (e.g. in cross-domain iframe context)

class MemoryStorage {
  private store = new Map<string, string>();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }

  keys(): string[] {
    return Array.from(this.store.keys());
  }
}

class SafeStorageWrapper {
  private isAvailable: boolean = false;
  private fallbackStore: MemoryStorage | null = null;

  constructor() {
    try {
      // Test if localStorage is accessible and writable.
      // Simply checking window.localStorage can throw a SecurityError in some iframe settings.
      if (typeof window !== 'undefined' && 'localStorage' in window) {
        const testKey = '__storage_test__';
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        this.isAvailable = true;
      }
    } catch (e) {
      console.warn("localStorage is not available/blocked. Using in-memory fallback storage.", e);
      this.isAvailable = false;
      this.fallbackStore = new MemoryStorage();
    }
  }

  getItem(key: string): string | null {
    if (this.isAvailable) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        console.warn("Failed to getItem from localStorage, using fallback:", e);
      }
    }
    if (!this.fallbackStore) {
      this.fallbackStore = new MemoryStorage();
    }
    return this.fallbackStore.getItem(key);
  }

  setItem(key: string, value: string): void {
    if (this.isAvailable) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch (e) {
        console.warn("Failed to setItem in localStorage, using fallback:", e);
      }
    }
    if (!this.fallbackStore) {
      this.fallbackStore = new MemoryStorage();
    }
    this.fallbackStore.setItem(key, value);
  }

  removeItem(key: string): void {
    if (this.isAvailable) {
      try {
        window.localStorage.removeItem(key);
        return;
      } catch (e) {
        console.warn("Failed to removeItem from localStorage, using fallback:", e);
      }
    }
    if (this.fallbackStore) {
      this.fallbackStore.removeItem(key);
    }
  }

  clear(): void {
    if (this.isAvailable) {
      try {
        window.localStorage.clear();
        return;
      } catch (e) {
        console.warn("Failed to clear localStorage, using fallback:", e);
      }
    }
    if (this.fallbackStore) {
      this.fallbackStore.clear();
    }
  }

  keys(): string[] {
    if (this.isAvailable) {
      try {
        return Object.keys(window.localStorage);
      } catch (e) {
        console.warn("Failed to retrieve keys from localStorage, using fallback:", e);
      }
    }
    if (!this.fallbackStore) {
      this.fallbackStore = new MemoryStorage();
    }
    return this.fallbackStore.keys();
  }
}

export const safeStorage = new SafeStorageWrapper();
